<h2 mat-dialog-title fxLayout="row" fxLayoutAlign="space-between center">
  <span>Scratch pad</span>
  <mat-spinner *ngIf="loading" [diameter]="24"></mat-spinner>
</h2>

<mat-dialog-content class="scratch-content">
  <div class="scratch-panes" fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="16px">

    <!-- LEFT: what is in the scratch template and will render -->
    <div fxFlex="50" fxFlex.lt-md="100">
      <mat-card class="pane h-100">
        <mat-card-content>
          <div fxLayout="row" fxLayoutAlign="space-between center" class="m-b-10">
            <strong>In scratch (renders)</strong>
            <button mat-icon-button color="basic" matTooltip="Remove all themes from scratch"
              [disabled]="inScratch.length === 0" (click)="clearScratch()">
              <mat-icon>restart_alt</mat-icon>
            </button>
          </div>

          <div *ngIf="inScratch.length === 0" class="empty-pane text-warning">
            No themes yet — grab some from the right.
          </div>

          <div *ngFor="let item of inScratch">
            <style-template-card
              [id]="item?.id"
              [companyId]="item?.companyId"
              [name]="item?.displayName || item?.name"
              [thumbnailUrl]="item?.thumbnailUrl"
              [showClear]="true"
              (onClear)="remove(item?.id)">
            </style-template-card>
          </div>
        </mat-card-content>
      </mat-card>
    </div>

    <!-- RIGHT: themes to grab from — one tab per home picked from browse, plus the full theme tree -->
    <div fxFlex="50" fxFlex.lt-md="100">
      <mat-card class="pane h-100">
        <mat-card-content>
          <div fxLayout="row" fxLayoutAlign="space-between center" fxLayoutGap="8px" class="m-b-10">
            <!-- Source chips wrap cleanly as more are added; a plain button row avoids the
                 mat-button-toggle-group's segmented border breaking on wrap. -->
            <div class="source-chips" fxLayout="row wrap" fxLayoutAlign="start center">
              <button *ngIf="popularStyles.length > 0" type="button" class="source-chip"
                [class.selected]="selectedSourceId === popularThemesSource"
                (click)="selectedSourceId = popularThemesSource" matTooltip="Themes you use most">
                <mat-icon class="chip-icon">star</mat-icon> Popular
              </button>
              <button *ngIf="gardenStyles.length > 0" type="button" class="source-chip"
                [class.selected]="selectedSourceId === gardensSource"
                (click)="selectedSourceId = gardensSource" matTooltip="Gardens published from situSCAPE">
                <mat-icon class="chip-icon">local_florist</mat-icon> Gardens
              </button>
              <button *ngFor="let source of sources" type="button" class="source-chip"
                [class.selected]="selectedSourceId === source.templateId"
                (click)="selectedSourceId = source.templateId"
                [matTooltip]="source.isPreviousScratch ? 'The themes this scratch render used' : source.templateName">
                <mat-icon *ngIf="source.isPreviousScratch" class="chip-icon">history</mat-icon>
                {{ sourceLabel(source) }}
              </button>
              <button type="button" class="source-chip"
                [class.selected]="selectedSourceId === allThemesSource"
                (click)="selectedSourceId = allThemesSource">All themes</button>
            </div>
            <div class="source-actions" fxLayout="row" fxLayoutGap="8px">
              <button mat-stroked-button color="basic" (click)="viewPreviousScratches()"
                [disabled]="!scratchId"
                matTooltip="See every render your scratch pad has produced">
                <mat-icon>history</mat-icon> Previous scratches
              </button>
              <button mat-stroked-button color="accent" (click)="addSourceFromBrowse()"
                matTooltip="Pick another home from situBROWSE to list its themes">
                <mat-icon>add</mat-icon> Add home
              </button>
            </div>
          </div>

          <!-- The themes this user reaches for most (from their own scratch render history) -->
          <ng-container *ngIf="selectedSourceId === popularThemesSource">
            <div fxLayout="row" fxLayoutAlign="start center" class="m-b-10">
              <small class="text-info">Your most-used themes</small>
            </div>
            <div *ngFor="let styleNode of popularStyles" class="pick-row"
              [class.already-in]="isInScratch(styleNode?.id)" (click)="addFromTree(styleNode)">
              <style-template-card
                [id]="styleNode?.id"
                [companyId]="styleNode?.companyId"
                [name]="styleNode?.displayName || styleNode?.themeName"
                [thumbnailUrl]="styleNode?.thumbnailUrl"
                [readonly]="isInScratch(styleNode?.id)">
              </style-template-card>
              <span class="pick-hint">{{ isInScratch(styleNode?.id) ? '✓ in scratch' : '→ grab' }}</span>
            </div>
          </ng-container>

          <!-- Gardens published from situSCAPE (LandscapingThemes styles) -->
          <ng-container *ngIf="selectedSourceId === gardensSource">
            <div fxLayout="row" fxLayoutAlign="start center" class="m-b-10">
              <small class="text-info">Published gardens from situSCAPE</small>
            </div>
            <div *ngFor="let styleNode of gardenStyles" class="pick-row"
              [class.already-in]="isInScratch(styleNode?.id)" (click)="addFromTree(styleNode)">
              <style-template-card
                [id]="styleNode?.id"
                [companyId]="styleNode?.companyId"
                [name]="styleNode?.displayName || styleNode?.themeName"
                [thumbnailUrl]="styleNode?.thumbnailUrl"
                [readonly]="isInScratch(styleNode?.id)">
              </style-template-card>
              <span class="pick-hint">{{ isInScratch(styleNode?.id) ? '✓ in scratch' : '→ grab' }}</span>
            </div>
          </ng-container>

          <!-- A picked home's themes, or the themes behind one of this user's past scratch renders -->
          <ng-container *ngIf="selectedSourceId !== allThemesSource && selectedSourceId !== popularThemesSource && selectedSourceId !== gardensSource && selectedSource as source">
            <div fxLayout="row" fxLayoutAlign="space-between center" class="m-b-10">
              <small class="text-info" *ngIf="source.isPreviousScratch">
                What this scratch render used — {{ sourceLabel(source) }}
              </small>
              <small class="text-info" *ngIf="!source.isPreviousScratch">
                {{ source.homeName ? source.homeName + ' — ' : '' }}{{ source.templateName }}
              </small>
              <div>
                <button mat-button color="primary" [disabled]="source.styles.length === 0" (click)="takeAll()">
                  Take all
                </button>
                <button mat-icon-button color="basic" matTooltip="Remove this source from the list"
                  (click)="removeSource(source.templateId)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
            <div *ngIf="source.styles.length === 0" class="empty-pane">
              This style template has no themes.
            </div>
            <div *ngFor="let style of source.styles" class="pick-row"
              [class.already-in]="isInScratch(style?.id)" (click)="addFromSource(style)">
              <style-template-card
                [id]="style?.id"
                [companyId]="style?.companyId"
                [name]="style?.displayName || style?.name"
                [thumbnailUrl]="style?.thumbnailUrl"
                [readonly]="isInScratch(style?.id)">
              </style-template-card>
              <span class="pick-hint">{{ isInScratch(style?.id) ? '✓ in scratch' : '→ grab' }}</span>
            </div>
          </ng-container>

          <!-- Full theme tree -->
          <ng-container *ngIf="selectedSourceId === allThemesSource">
            <mat-accordion displayMode="default" multi>
              <ng-container *ngFor="let category of categoryData">
                <custom-expansion-panel
                  [title]="category?.name"
                  [subtitle]="category?.styles?.length"
                  [id]="'scratch-' + category.id"
                  [info]="category?.description">
                  <div *ngFor="let styleNode of category.styles" class="pick-row"
                    [class.already-in]="isInScratch(styleNode?.id)" (click)="addFromTree(styleNode)">
                    <style-template-card
                      [id]="styleNode?.id"
                      [companyId]="styleNode?.companyId"
                      [name]="styleNode?.displayName || styleNode?.themeName"
                      [thumbnailUrl]="styleNode?.thumbnailUrl"
                      [readonly]="isInScratch(styleNode?.id)">
                    </style-template-card>
                    <span class="pick-hint">{{ isInScratch(styleNode?.id) ? '✓ in scratch' : '→ grab' }}</span>
                  </div>
                </custom-expansion-panel>
              </ng-container>
            </mat-accordion>
          </ng-container>
        </mat-card-content>
      </mat-card>
    </div>

  </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="cancel()">Cancel</button>
  <button mat-flat-button color="accent" [disabled]="loading || !scratchId || inScratch.length === 0"
    (click)="renderScratch()"
    matTooltip="Save these themes to your scratch template and render it">
    <mat-icon>science</mat-icon> Render scratch
  </button>
</mat-dialog-actions>
